<template>
  <div class="modal fade show my-2" aria-labelledby="selectChequeApprovalModalTitle" style="display: block; background: #1c1d1c33">
    <div
      class="modal-dialog modal-dialog-centered"
      :style="dialogStyle"
      ref="dialogRef"
    >
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div
              class="d-flex justify-content-between px-2 my-1 align-item-center modal-drag-handle"
              @mousedown="onDragStart"
              :style="{ cursor: dragging ? 'grabbing' : 'grab' }"
            >
              <h4 class="mb-0 user-select-none">Update Cheque</h4>
              <button
                @click="onClickClose"
                @mousedown.stop
                type="button"
                class="btn-close"
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>

        <hr class="mt-0 mb-0">

        <div class="pb-2">
          <div class="row mx-2 mt-1">
            <div class="col-12">
              <label class="form-label">Bank Account</label>
              <v-select
                placeholder="Select Bank Account"
                v-model="selectedAccountHeadId"
                :options="cashAndBank"
                label="name"
                :reduce="item => item.id"
              />
            </div>

            <div class="col-12 mt-1">
              <label class="form-label">Cheque</label>
              <AsyncSelect
                placeholder="Select Cheque"
                v-model="selectedCheque"
                :api-service="fetchChequeRegisterData"
                :additional-query="{ account_head_id: selectedAccountHeadId, status: 'active' }"
                :disabled="! selectedAccountHeadId"
                label="cheque_number"
                :format-label="option => option.cheque_number"
                :reduce="item => item"
              />
            </div>

            <div class="col-12 text-center mb-2 mt-2">
              <button
                :disabled="submitting"
                @click="onClickSave"
                type="button"
                class="btn btn-primary waves-effect waves-float waves-light me-1"
              >
                {{ submitting ? 'Submitting...' : 'Save' }}
              </button>
              <button
                :disabled="submitting"
                @click="onClickSaveAndPrint"
                type="button"
                class="btn btn-success waves-effect waves-float waves-light"
              >
                {{ submitting ? 'Submitting...' : 'Save & Print' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ChequePrint
      v-if="showPrint && printItem"
      :item="printItem"
      @onPrinted="onPrinted"
      @close="onPrintClose"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, inject } from 'vue'
import { useRoute } from 'vue-router'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ChequePrint from '@/views/page/company/core-accounting/cheque-register/ChequePrint'
import handleAccounting from '@/services/modules/accounting'
import handlePayment from '@/services/modules/payment'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'

const props = defineProps({
  ledgerMasterId: { type: Number, required: true },
  preselectedChequeId: { type: Number, default: null },
  preselectedAccountHeadId: { type: Number, default: null }
})
const emit = defineEmits(['close', 'approved'])

const showError = inject('showError')
const showSuccess = inject('showSuccess')

const $route = useRoute()
const { fetchChequeRegisterData, fetchSingleChequeRegister } = handleAccounting()
const { fetchCashAndBank } = handlePayment()
const { updateLedgerCheque } = handlePaymentRequisition()

const selectedAccountHeadId = ref(null)
const selectedCheque = ref(null)
const cashAndBank = ref([])
const submitting = ref(false)
const showPrint = ref(false)
const printItem = ref(null)

const dialogRef = ref(null)
const dragging = ref(false)
const offsetX = ref(0)
const offsetY = ref(0)
const dragStart = { x: 0, y: 0, baseX: 0, baseY: 0 }

const dialogStyle = computed(() => {
  if (offsetX.value === 0 && offsetY.value === 0) return {}
  return {
    transform: `translate(${offsetX.value}px, ${offsetY.value}px)`,
    margin: '1.75rem auto'
  }
})

const onDragStart = (event) => {
  if (event.button !== 0) return
  dragging.value = true
  dragStart.x = event.clientX
  dragStart.y = event.clientY
  dragStart.baseX = offsetX.value
  dragStart.baseY = offsetY.value
  document.addEventListener('mousemove', onDragMove)
  document.addEventListener('mouseup', onDragEnd)
  event.preventDefault()
}

const onDragMove = (event) => {
  if (! dragging.value) return
  offsetX.value = dragStart.baseX + (event.clientX - dragStart.x)
  offsetY.value = dragStart.baseY + (event.clientY - dragStart.y)
}

const onDragEnd = () => {
  dragging.value = false
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
}

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onDragMove)
  document.removeEventListener('mouseup', onDragEnd)
})

watch(selectedAccountHeadId, (newVal, oldVal) => {
  if (oldVal === null) return
  if (newVal === props.preselectedAccountHeadId) return
  selectedCheque.value = null
})

onMounted(async () => {
  const companyId = $route.params.companyId
  const res = await fetchCashAndBank(companyId).catch(() => null)
  if (res && res.data) cashAndBank.value = res.data

  if (! props.preselectedChequeId) return
  const query = `?company_id=${companyId}`
  const chequeRes = await fetchSingleChequeRegister(props.preselectedChequeId, query).catch(() => null)
  if (! chequeRes || ! chequeRes.status) return
  selectedAccountHeadId.value = chequeRes.data.account_head_id
  selectedCheque.value = chequeRes.data
})

const saveCheque = async () => {
  if (! selectedCheque.value) {
    showError('Please select a cheque.')
    return null
  }
  submitting.value = true
  try {
    const res = await updateLedgerCheque(props.ledgerMasterId, {
      company_id: $route.params.companyId,
      cheque_register_id: selectedCheque.value.id,
    })
    if (! res.status) {
      showError(res.message)
      return null
    }
    showSuccess(res.message)
    emit('approved')
    return res.data
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
    return null
  } finally {
    submitting.value = false
  }
}

const onClickSave = async () => {
  const cheque = await saveCheque()
  if (! cheque) return
  emit('close')
}

const onClickSaveAndPrint = async () => {
  const cheque = await saveCheque()
  if (! cheque) return
  printItem.value = cheque
  showPrint.value = true
}

const onPrinted = () => {
  emit('approved')
}

const onPrintClose = () => {
  showPrint.value = false
  printItem.value = null
  emit('close')
}

const onClickClose = () => emit('close')
</script>
